<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AiTokenUsage from './AiTokenUsage.vue';
import type { EntityRevision, EntityVersion } from '../types/entities';

const props = defineProps<{
  versions: EntityVersion[];
  modelVersionId: string;
  modelRevisionId: string | null;
  createVersion: (input: { sourceVersionId: string; sourceRevisionId?: string | null; name?: string }) => Promise<unknown>;
  renameVersion: (versionId: string, name: string) => Promise<unknown>;
  activateVersion: (versionId: string) => Promise<unknown>;
  archiveVersion: (versionId: string) => Promise<unknown>;
  deleteVersion: (versionId: string) => Promise<unknown>;
  compareVersions: (leftVersionId: string, rightVersionId: string) => Promise<{ analysis: string; model: string; usage: { inputTokens: number; outputTokens: number; totalTokens: number } | null }>;
  saveRevision: () => Promise<unknown>;
}>();
const emit = defineEmits<{ browse: [versionId: string, revisionId: string | null]; changed: [] }>();

const showArchived = ref(false);
const newName = ref('');
const renameName = ref('');
const busy = ref('');
const error = ref('');
const compareOpen = ref(false);
const leftId = ref('');
const rightId = ref('');
const comparison = ref<{ analysis: string; model: string; usage: { inputTokens: number; outputTokens: number; totalTokens: number } | null } | null>(null);

const visibleVersions = computed(() => props.versions.filter((version) => showArchived.value || version.status !== 'archived'));
const selected = computed(() => props.versions.find((version) => version.id === props.modelVersionId) ?? props.versions[0]);
const selectedRevision = computed<EntityRevision | null>(() => selected.value?.revisions.find((revision) => revision.id === props.modelRevisionId) ?? null);

watch(selected, (value) => { renameName.value = value?.name ?? ''; }, { immediate: true });
watch(() => props.versions, (versions) => {
  const candidates = versions.filter((version) => version.status !== 'archived');
  if (!leftId.value) leftId.value = candidates[1]?.id ?? candidates[0]?.id ?? '';
  if (!rightId.value) rightId.value = candidates[0]?.id ?? '';
}, { immediate: true });

function escapeHtml(value: string) { return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[character]!)); }
function renderMarkdown(value: string) {
  const escaped = escapeHtml(value).replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>').replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^###\s+(.+)$/gm, '<h4>$1</h4>').replace(/^##\s+(.+)$/gm, '<h3>$1</h3>').replace(/^#\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>').replace(/(?:<li>.*<\/li>\n?)+/g, (list) => `<ul>${list}</ul>`)
    .replace(/^(?!<[hupl]|<pre)(.+)$/gm, '<p>$1</p>').replace(/<p>\s*<\/p>/g, '');
  return escaped;
}

async function run(key: string, action: () => Promise<unknown>) {
  busy.value = key; error.value = '';
  try { await action(); emit('changed'); }
  catch (reason) { error.value = reason instanceof Error ? reason.message : 'Die Aktion ist fehlgeschlagen.'; }
  finally { busy.value = ''; }
}
function browseVersion(event: Event) { emit('browse', (event.target as HTMLSelectElement).value, null); }
function browseRevision(event: Event) { emit('browse', props.modelVersionId, (event.target as HTMLSelectElement).value || null); }
async function create() {
  if (!selected.value) return;
  await run('create', () => props.createVersion({ sourceVersionId: selected.value!.id, sourceRevisionId: selectedRevision.value?.id ?? null, name: newName.value.trim() || undefined }));
  newName.value = '';
}
async function saveRevision() { await run('revision', props.saveRevision); }
async function rename() { if (selected.value && renameName.value.trim()) await run('rename', () => props.renameVersion(selected.value!.id, renameName.value)); }
async function activate() { if (selected.value) await run('activate', () => props.activateVersion(selected.value!.id)); }
async function archive() { if (selected.value && confirm(`„${selected.value.name}“ archivieren?`)) await run('archive', () => props.archiveVersion(selected.value!.id)); }
async function remove() { if (selected.value && confirm(`„${selected.value.name}“ dauerhaft löschen? Historisch benötigte Fassungen werden geschützt.`)) await run('delete', () => props.deleteVersion(selected.value!.id)); }
async function compare() {
  if (!leftId.value || !rightId.value || leftId.value === rightId.value) { error.value = 'Wähle zwei unterschiedliche Fassungen.'; return; }
  busy.value = 'compare'; error.value = ''; comparison.value = null;
  try { comparison.value = await props.compareVersions(leftId.value, rightId.value); }
  catch (reason) { error.value = reason instanceof Error ? reason.message : 'Der Vergleich ist fehlgeschlagen.'; }
  finally { busy.value = ''; }
}
</script>

<template>
  <div class="entity-version-panel">
    <div class="entity-version-picker">
      <label>Fassung<select :value="modelVersionId" @change="browseVersion"><option v-for="version in visibleVersions" :key="version.id" :value="version.id">{{ version.name }} · {{ version.status === 'active' ? 'Master' : version.status === 'archived' ? 'Archiv' : 'Gespeichert' }}</option></select></label>
      <label>Revision<select :value="modelRevisionId ?? ''" @change="browseRevision"><option value="">Aktueller Stand</option><option v-for="revision in selected?.revisions ?? []" :key="revision.id" :value="revision.id">Revision {{ revision.revision }} · {{ new Date(revision.createdAt).toLocaleString('de-DE') }}</option></select></label>
    </div>
    <label class="version-archive-toggle"><input v-model="showArchived" type="checkbox" /> Archivierte Fassungen anzeigen</label>
    <button type="button" class="version-primary" :disabled="Boolean(busy)" @click="saveRevision">{{ busy === 'revision' ? 'Speichert …' : '+ Revision speichern' }}</button>
    <div class="entity-version-create"><input v-model="newName" placeholder="Name der neuen Fassung (optional)" @keydown.enter.prevent="create" /><button type="button" :disabled="Boolean(busy)" @click="create">+ Neue Fassung</button></div>
    <div v-if="selected" class="entity-version-actions">
      <input v-model="renameName" aria-label="Fassungsname" @keydown.enter.prevent="rename" />
      <button type="button" :disabled="Boolean(busy)" @click="rename">Umbenennen</button>
      <button v-if="selected.status !== 'active'" type="button" class="version-master" :disabled="Boolean(busy)" @click="activate">{{ selected.status === 'archived' ? 'Wiederherstellen & Master' : 'Als Master' }}</button>
      <button type="button" :disabled="Boolean(busy)" @click="archive">Archivieren</button>
      <button type="button" class="version-delete" :disabled="Boolean(busy)" @click="remove">Löschen</button>
    </div>
    <button type="button" class="entity-compare-toggle" @click="compareOpen = !compareOpen">{{ compareOpen ? 'Vergleich schließen' : '⇄ Zwei Fassungen vergleichen' }}</button>
    <div v-if="compareOpen" class="entity-version-compare">
      <div><select v-model="leftId"><option v-for="version in versions" :key="version.id" :value="version.id">{{ version.name }}</option></select><select v-model="rightId"><option v-for="version in versions" :key="version.id" :value="version.id">{{ version.name }}</option></select><button type="button" :disabled="busy === 'compare'" @click="compare">{{ busy === 'compare' ? 'Luna analysiert …' : 'Mit GPT-5.6 Luna vergleichen' }}</button></div>
      <article v-if="comparison" class="segment-markdown-output" v-html="renderMarkdown(comparison.analysis)" />
      <AiTokenUsage v-if="comparison" :usage="comparison.usage" />
    </div>
    <small v-if="error" class="dialog-error">{{ error }}</small>
  </div>
</template>
